// src/pages/IletisimPage.js
import React from "react";
import GlobalStyles from "@mui/material/GlobalStyles";
import { useNotify } from "react-admin";
import ResimUpload from "../ResimUpload";
import { API_BASE, UPLOAD_BASE, ORIGIN_BASE } from '../config';


/**
 * İletişim — tek sayfa (tek kayıt) form
 * GET  /api/iletisim      -> mevcut kayıt (yoksa boş)
 * POST /api/iletisim      -> ilk oluşturma
 * PUT  /api/iletisim/:id  -> güncelleme
 */

const ALLOWED = ["tr", "en", "de"];
const bust = (u, v) => (u ? `${u}${u.includes("?") ? "&" : "?"}v=${v}` : "");


const T = {
  tr: {
    pageTitle: "📞 İletişim Bilgileri",
    save: "Kaydet",
    saving: "Kaydediliyor…",
    savedOk: "✅ İletişim bilgileri kaydedildi",
    savedErr: "❌ Kayıt hatası",

    // bölümler
    general: "Genel",
addressHours: "Adres & Çalışma Saatleri ",
    contact: "İletişim Kanalları",
    social: "Sosyal & Web",
    finance: "Bağış / Finans (IBAN)",
    map: "Harita",

    // genel
    name: "Kurum Adı",
    logo: "Logo / Görsel",
    allActive: "Kart Aktif mi?",
    active: "Aktif",
    add: "Ekle",
    remove: "Sil",
    preview: "Önizleme",

    // dinamik etiketler
    addressLabel: (code) => `Adres (${code.toUpperCase()})`,
    hoursLabel: (code) => `Çalışma Saatleri (${code.toUpperCase()})`,

    // kanallar
    phone: "Telefon",
    whatsapp: "WhatsApp",
    email: "E-posta",

    // sosyal
    instagram: "Instagram",
    website: "Web Sitesi",
    facebook: "Facebook",
    twitter: "X (Twitter)",
    linkedin: "LinkedIn",
    youtube: "YouTube",

    // iban
    addIban: "+ IBAN Ekle",
    bankName: "Banka Adı",
    accountName: "Hesap Adı/Açıklama",
    iban: "IBAN",

    // harita
    mapsUrl: "Google Maps URL (https://maps.google.com/?q=...)",
  },
  en: {
    pageTitle: "📞 Contact",
    save: "Save",
    saving: "Saving…",
    savedOk: "✅ Contact info saved",
    savedErr: "❌ Save error",
    general: "General",
    addressHours: "Address & Opening Hours",
    contact: "Channels",
    social: "Social & Web",
    finance: "Donations / Finance (IBAN)",
    map: "Map",
    name: "Organization Name",
    logo: "Logo / Image",
    allActive: "Card Active?",
    active: "Active",
    add: "Add",
    remove: "Remove",
    preview: "Preview",
    addressLabel: (code) => `Address (${code.toUpperCase()})`,
    hoursLabel: (code) => `Opening Hours (${code.toUpperCase()})`,
    phone: "Phone",
    whatsapp: "WhatsApp",
    email: "Email",
    instagram: "Instagram",
    website: "Website",
    facebook: "Facebook",
    twitter: "X (Twitter)",
    linkedin: "LinkedIn",
    youtube: "YouTube",
    addIban: "+ Add IBAN",
    bankName: "Bank Name",
    accountName: "Account/Note",
    iban: "IBAN",
    mapsUrl: "Google Maps URL (https://maps.google.com/?q=...)",
  },
  de: {
    pageTitle: "📞 Kontakt",
    save: "Speichern",
    saving: "Wird gespeichert…",
    savedOk: "✅ Kontakt gespeichert",
    savedErr: "❌ Fehler beim Speichern",
    general: "Allgemein",
    addressHours: "Adresse & Öffnungszeiten",
    contact: "Kanäle",
    social: "Sozial & Web",
    finance: "Spenden / Finanzen (IBAN)",
    map: "Karte",
    name: "Name der Organisation",
    logo: "Logo / Bild",
    allActive: "Karte aktiv?",
    active: "Aktiv",
    add: "Hinzufügen",
    remove: "Löschen",
    preview: "Vorschau",
    addressLabel: (code) => `Adresse (${code.toUpperCase()})`,
    hoursLabel: (code) => `Öffnungszeiten (${code.toUpperCase()})`,
    phone: "Telefon",
    whatsapp: "WhatsApp",
    email: "E-Mail",
    instagram: "Instagram",
    website: "Webseite",
    facebook: "Facebook",
    twitter: "X (Twitter)",
    linkedin: "LinkedIn",
    youtube: "YouTube",
    addIban: "+ IBAN hinzufügen",
    bankName: "Bankname",
    accountName: "Konto/Notiz",
    iban: "IBAN",
    mapsUrl: "Google Maps URL (https://maps.google.com/?q=...)",
  },
};

function useUiTexts() {
  const [uiLang, setUiLang] = React.useState(
    () => (localStorage.getItem("app_lang") || "tr").slice(0, 2).toLowerCase()
  );
  React.useEffect(() => {
    const sync = () => {
      const l = (localStorage.getItem("app_lang") || "tr").slice(0, 2).toLowerCase();
      setUiLang(ALLOWED.includes(l) ? l : "tr");
    };
    window.addEventListener("app_lang_change", sync);
    window.addEventListener("storage", sync);
    window.addEventListener("focus", sync);
    return () => {
      window.removeEventListener("app_lang_change", sync);
      window.removeEventListener("storage", sync);
      window.removeEventListener("focus", sync);
    };
  }, []);
  return T[ALLOWED.includes(uiLang) ? uiLang : "tr"];
}

/* ---------------- Helpers: UI ---------------- */
const Toggle = ({ label, checked, onChange }) => (
  <label
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 14,
      padding: "6px 10px",
      borderRadius: 6,
      border: "1px solid #e5e7eb",
      background: "#fff",
    }}
  >
    <span style={{ fontWeight: 600 }}>{label}</span>
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <span style={{ fontSize: 12, color: "#475569", minWidth: 38, textAlign: "right" }}>
        {checked ? "Açık" : "Kapalı"}
      </span>
      <input
        type="checkbox"
        checked={!!checked}
        onChange={(e) => onChange(e.target.checked)}
        style={{
          transform: "scale(1.4)",
          marginLeft: 4,
          cursor: "pointer",
        }}
      />
    </div>
  </label>
);

const Row = ({ label, value, onChange, placeholder, right }) => (
  <div style={{ display: "grid", gridTemplateColumns: right ? "1fr auto" : "1fr", gap: 8 }}>
    <label className="block font-medium mb-1" style={{ marginBottom: 6 }}>{label}</label>
    <input
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      className="w-full border rounded px-3 py-2"
      placeholder={placeholder || ""}
    />
    {right}
  </div>
);

const Area = ({ label, value, onChange, rows = 4, placeholder }) => (
  <div>
    <label className="block font-medium mb-1" style={{ marginBottom: 6 }}>{label}</label>
    <textarea
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      className="w-full border rounded px-3 py-2"
      rows={rows}
      placeholder={placeholder || ""}
    />
  </div>
);

const Section = ({ title, children }) => (
  <div style={{ background: "#fff", borderRadius: 8, padding: 16, border: "1px solid #e5e7eb" }}>
    <h3 style={{ marginTop: 0, marginBottom: 12, fontWeight: 800, color: "#0f172a" }}>{title}</h3>
    <div className="space-y-4">{children}</div>
  </div>
);

const IbanItem = ({ item, onChange, onRemove, TT }) => (
  <div
    style={{
      border: "1px solid #e5e7eb",
      borderRadius: 8,
      padding: 12,
      display: "grid",
      gridTemplateColumns: "1fr 1fr 1fr auto",
      gap: 8,
      alignItems: "center",
    }}
  >
    <Row label={TT.bankName} value={item.banka_adi} onChange={(v) => onChange({ ...item, banka_adi: v })} />
    <Row label={TT.accountName} value={item.hesap_adi} onChange={(v) => onChange({ ...item, hesap_adi: v })} />
    <Row label={TT.iban} value={item.iban} onChange={(v) => onChange({ ...item, iban: v })} />
    <div style={{ display: "flex", gap: 12, alignItems: "center", justifyContent: "flex-end" }}>
      <Toggle label={TT.active} checked={!!item.aktif} onChange={(v) => onChange({ ...item, aktif: v })} />
      <button
        type="button"
        onClick={onRemove}
        style={{ padding: "6px 10px", borderRadius: 6, border: "1px solid #ef4444", color: "#ef4444" }}
      >
        {TT.remove}
      </button>
    </div>
  </div>
);

/* -----------------------------------------------------------
   PAYLAŞILAN FORM (list/edit/create hepsi için aynı ekran)
   - Aktif diller: localStorage 'kurum_ayarlar.aktif_diller'
   - Adres + Çalışma Saati: tek aktif butonu
----------------------------------------------------------- */
function SharedIletisimForm({ mode = "list" }) {
  const TT = useUiTexts();
  const notify = useNotify();

  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [id, setId] = React.useState(null);
  const [ver, setVer] = React.useState(0); // duyurular gibi cache-bust

  const [aktifDiller, setAktifDiller] = React.useState(ALLOWED);

  // Ayarlar'dan aktif dilleri çek
  React.useEffect(() => {
    try {
      const s = localStorage.getItem("kurum_ayarlar");
      if (s) {
        const j = JSON.parse(s);
        const arr = Array.isArray(j?.aktif_diller) ? j.aktif_diller.map((x) => x?.toLowerCase().slice(0,2)) : ALLOWED;
        const filtered = arr.filter((x) => ALLOWED.includes(x));
        setAktifDiller(filtered.length ? filtered : ALLOWED);
      } else {
        setAktifDiller(ALLOWED);
      }
    } catch {
      setAktifDiller(ALLOWED);
    }
  }, []);

  // form state
  const [form, setForm] = React.useState({
    // kart genel
    kart_aktif: true,

    // temel
    kurum_adi: "", kurum_adi_aktif: true,
    logo_url: "", logo_aktif: true,

    // adres & saatler (dil başına)
    adres_tr: "", adres_tr_aktif: true, saat_tr: "", saat_tr_aktif: true,
    adres_en: "", adres_en_aktif: true, saat_en: "", saat_en_aktif: true,
    adres_de: "", adres_de_aktif: true, saat_de: "", saat_de_aktif: true,

    // iletişim kanalları
    telefon: "", telefon_aktif: true,
    whatsapp: "", whatsapp_aktif: true,
    email: "", email_aktif: true,

    // sosyal & web
    instagram: "", instagram_aktif: true,
    website: "", website_aktif: true,
    facebook: "", facebook_aktif: false,
    twitter: "", twitter_aktif: false,
    linkedin: "", linkedin_aktif: false,
    youtube: "", youtube_aktif: false,

    // iban listesi
    ibans: [], // [{ banka_adi, hesap_adi, iban, aktif }]
    // harita
    harita_url: "", harita_aktif: true,
  });

  const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  // yükleme
  React.useEffect(() => {
    const load = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await fetch(`${API_BASE}/iletisim`, {
          headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
        });

        if (res.ok) {
          const json = await res.json();
          if (json && Object.keys(json).length) {
            setId(json.id);
            setForm((p) => ({
              ...p,
              ...Object.fromEntries(Object.entries(json).map(([k, v]) => [k, v == null ? "" : v])),
              ibans: Array.isArray(json.ibans) ? json.ibans.map((x) => ({ ...x, aktif: !!x.aktif })) : [],
            }));
            setVer((n) => n + 1); // mevcut görsel önizlemesi tazelensin
          }
        }
      } catch {}
      setLoading(false);
    };
    load();
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      const payload = {
        ...form,
        ibans: Array.isArray(form.ibans) ? form.ibans : [],
      };
      let res;
      if (id) {
        res = await fetch(`${API_BASE}/iletisim/${id}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify(payload),
        });

      } else {
        res = await fetch(`${API_BASE}/iletisim`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify(payload),
        });
      }
      if (res.ok) {
        const j = await res.json().catch(() => ({}));
        if (j?.id) setId(j.id);
        notify(TT.savedOk, { type: "success" });
      } else {
        notify(TT.savedErr, { type: "warning" });
      }
    } catch {
      notify(TT.savedErr, { type: "warning" });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-6">Yükleniyor…</div>;

  const titleSuffix =
    mode === "create" ? " — Yeni" : mode === "edit" ? " — Düzenle" : "";

  // Dile göre alan adı oluşturma yardımcıları
  const aKey = (code) => `adres_${code}`;
  const hKey = (code) => `saat_${code}`;
  const aAct = (code) => `adres_${code}_aktif`;
  const hAct = (code) => `saat_${code}_aktif`;

  return (
    <>
      <GlobalStyles
        styles={{
          ".MuiSnackbarContent-message": { fontSize: "1.05rem", fontWeight: 800 },
          ".RaNotification-content": { fontSize: "1.05rem", fontWeight: 800 },
        }}
      />

      <div className="p-6 w-full bg-transparent">
        <h2 className="text-2xl font-extrabold mb-4">
          {TT.pageTitle}{titleSuffix}
        </h2>

        <form onSubmit={save} className="space-y-4">
          {/* Genel */}
          <Section title={TT.general}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 180px", gap: 16 }}>
              <Row label={TT.name} value={form.kurum_adi} onChange={(v) => set({ kurum_adi: v })} />
              <Toggle label={TT.active} checked={form.kurum_adi_aktif} onChange={(v) => set({ kurum_adi_aktif: v })} />
            </div>

            {/* LOGO / Görsel — (sadece bu bölüm düzenlendi) */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 180px", gap: 16, alignItems: "center" }}>
              <Row
                label={TT.logo}
                value={form.logo_url}
                onChange={(v) => set({ logo_url: v })}
                right={
                  <ResimUpload
                    onUpload={(url) => {
                      set({ logo_url: url });
                      setVer((n) => n + 1);
                    }}
                    defaultPreview={bust(form.logo_url, ver)}
                    label="Yükle"
                  />
                }
              />
              <Toggle label={TT.active} checked={form.logo_aktif} onChange={(v) => set({ logo_aktif: v })} />
            </div>

            <Toggle label={TT.allActive} checked={form.kart_aktif} onChange={(v) => set({ kart_aktif: v })} />
          </Section>

          {/* Adres & Saatler (aktif dillere göre) */}
          <Section title={TT.addressHours}>
            {aktifDiller.map((code) => {
              const addrKey = aKey(code);
              const hourKey = hKey(code);
              const addrActKey = aAct(code);
              const hourActKey = hAct(code);
              const combinedActive = !!(form[addrActKey] && form[hourActKey]);

              return (
                <div
                  key={code}
                  style={{
                    border: "1px solid #e5e7eb",
                    borderRadius: 8,
                    padding: 12,
                    marginBottom: 12,
                    background: "#fafafa",
                  }}
                >
                  <div style={{ marginBottom: 10, display: "grid", gridTemplateColumns: "1fr 180px", gap: 16 }}>
                    <div style={{ fontWeight: 700, color: "#0f172a" }}>
                      {code.toUpperCase()}
                    </div>
                    {/* Tek buton: hem adres_*_aktif hem saat_*_aktif kontrol */}
                    <Toggle
                      label={`${TT.active} — ${code.toUpperCase()}`}
                      checked={combinedActive}
                      onChange={(v) => set({ [addrActKey]: v, [hourActKey]: v })}
                    />
                  </div>

                  <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 12 }}>
                    <Area
                      label={TT.addressLabel(code)}
                      value={form[addrKey]}
                      onChange={(v) => set({ [addrKey]: v })}
                    />
                    <Area
                      label={TT.hoursLabel(code)}
                      value={form[hourKey]}
                      onChange={(v) => set({ [hourKey]: v })}
                    />
                  </div>
                </div>
              );
            })}
          </Section>

          {/* İletişim Kanalları */}
          <Section title={TT.contact}>
            {[
              ["telefon", TT.phone],
              ["whatsapp", TT.whatsapp],
              ["email", TT.email],
            ].map(([key, label]) => (
              <div key={key} style={{ display: "grid", gridTemplateColumns: "1fr 180px", gap: 16 }}>
                <Row label={label} value={form[key]} onChange={(v) => set({ [key]: v })} />
                <Toggle label={TT.active} checked={form[`${key}_aktif`]} onChange={(v) => set({ [`${key}_aktif`]: v })} />
              </div>
            ))}
          </Section>

          {/* Sosyal & Web */}
          <Section title={TT.social}>
            {[
              ["instagram", TT.instagram],
              ["website", TT.website],
              ["facebook", TT.facebook],
              ["twitter", TT.twitter],
              ["linkedin", TT.linkedin],
              ["youtube", TT.youtube],
            ].map(([key, label]) => (
              <div key={key} style={{ display: "grid", gridTemplateColumns: "1fr 180px", gap: 16 }}>
                <Row
                  label={label}
                  value={form[key]}
                  onChange={(v) => set({ [key]: v })}
                  placeholder={key === "instagram" ? "https://instagram.com/..." : "https://..."}
                />
                <Toggle label={TT.active} checked={form[`${key}_aktif`]} onChange={(v) => set({ [`${key}_aktif`]: v })} />
              </div>
            ))}
          </Section>

          {/* IBAN listesi */}
          <Section title={TT.finance}>
            <div className="space-y-3">
              {(form.ibans || []).map((item, idx) => (
                <IbanItem
                  key={idx}
                  item={item}
                  TT={TT}
                  onChange={(updated) => {
                    const arr = [...(form.ibans || [])];
                    arr[idx] = updated;
                    set({ ibans: arr });
                  }}
                  onRemove={() => {
                    const arr = [...(form.ibans || [])];
                    arr.splice(idx, 1);
                    set({ ibans: arr });
                  }}
                />
              ))}
            </div>
            <div style={{ marginTop: 10 }}>
              <button
                type="button"
                onClick={() =>
                  set({
                    ibans: [
                      ...(form.ibans || []),
                      { banka_adi: "", hesap_adi: "", iban: "", aktif: true },
                    ],
                  })
                }
                className="px-4 py-2 rounded text-white"
                style={{ backgroundColor: "#16a34a" }}
              >
                {TT.addIban}
              </button>
            </div>
          </Section>

          {/* Harita */}
          <Section title={TT.map}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 180px", gap: 16 }}>
              <Row
                label={TT.mapsUrl}
                value={form.harita_url}
                onChange={(v) => set({ harita_url: v })}
                placeholder="https://maps.google.com/?q=..."
              />
              <Toggle label={TT.active} checked={form.harita_aktif} onChange={(v) => set({ harita_aktif: v })} />
            </div>
            {form.harita_url && (
              <div style={{ marginTop: 12 }}>
                <div style={{ fontSize: 12, color: "#475569", marginBottom: 6 }}>{TT.preview}</div>
                <iframe
                  title="map"
                  src={form.harita_url}
                  width="100%"
                  height="280"
                  style={{ border: 0, borderRadius: 8 }}
                  loading="lazy"
                />
              </div>
            )}
          </Section>

          {/* Kaydet */}
          <div className="pt-2">
            <button
              type="submit"
              disabled={saving}
              className="px-6 py-2 rounded text-white disabled:opacity-60"
              style={{ backgroundColor: "#2563eb" }}
            >
              {saving ? TT.saving : TT.save}
            </button>
          </div>
        </form>
      </div>
    </>
  );
}

/* -----------------------------------------------------------
   REACT-ADMIN İÇİN NAMED EXPORT’LAR
----------------------------------------------------------- */
export function IletisimPage(props) {
  return <SharedIletisimForm mode="list" {...props} />;
}

export function IletisimEdit(props) {
  return <SharedIletisimForm mode="edit" {...props} />;
}

export function IletisimCreate(props) {
  return <SharedIletisimForm mode="create" {...props} />;
}

// (opsiyonel) default export, liste gibi çalışsın
export default IletisimPage;
